import { produce } from 'immer';
import { filterProducts } from './filtering';
import { percentage, savings, savingsCeilingPercentage, savingsPercentage } from './shared-calculations';
import {
  findIncumbentProduct,
  getCoverageByOffer,
  incumbentAndCrosses,
  isIncumbentProduct,
  isConversionProduct,
} from './stateLocators';
import { productsToModel } from './stateModifiers';

// Calculate the amount we're going to spend based upon the percentage allocated to this particular
// product, how many units we bought of the incumbent product, and the price.
function calculatePlannedSpend(product: any, units: number) {
  return units * (product.allocation / 100.0) * product.unitPrice;
}

/**
 * Do all of the calculations for a given product, that includes breakouts at the offer level and
 * at the UNSPSC level as well so that we can do rollups later.
 *
 * @param product
 */
export function updateProductCalculations(product: any) {
  const continuedSpend =
    product.incumbentProduct.continuedSpend
  let continuedSpendAllocated = 0;
  let plannedSpend = 0;
  let excludedPlannedSpend = 0;

  product.offerCalculations = new Map<number | string, any>();
  product.unspscCalculations = new Map<number, any>();

  // This aggregates across the incumbent and all of the crossed products.
  for (let value of incumbentAndCrosses(product)) {
    // Remember that the yielded value is actually an object which _contains_ the product in question
    // (as well as other info). We have to dig in to get to the actual product.
    let incumbentOrCross = value.product;

    const excluded = incumbentOrCross.exclude; //spendStaus === 'CARVE_IN || CARVE_OUT'
    const unitsPurchased =
      'incumbent' in value
        ? value.incumbent.unitsPurchased
        : incumbentOrCross.unitsPurchased;

    continuedSpendAllocated +=
      (incumbentOrCross.allocation * continuedSpend) / 100;

    // We always add the amount to the planned spend. We do plan to spend that much money.
    let incumbentOrCrossPlannedSpend = calculatePlannedSpend(
      incumbentOrCross,
      unitsPurchased,
    );

    // Get per offer calculations from the Map of offer calculations on this product.
    let offerCalculations = product.offerCalculations.get(value.offerId) ?? {
      excludedPlannedSpend: 0,
      filteredPlannedSpend: 0,
      filteredExcludedPlannedSpend: 0,
      plannedSpend: 0,
      plannedUnits: 0,
    };

    // Note: I thought I could get rid of this but it seems it gets used for other rollups and
    // calculations.
    let unspscCalculations = product.unspscCalculations.get(
      incumbentOrCross.unspsc.id,
    ) ?? {
      excludedPlannedSpend: 0,
      plannedSpend: 0,

      excludedPlannedUnits: 0,
      plannedUnits: 0,
    };

    let allocatedUnits = (unitsPurchased * incumbentOrCross.allocation) / 100.0;

    plannedSpend += incumbentOrCrossPlannedSpend;
    offerCalculations.plannedSpend += incumbentOrCrossPlannedSpend;
    offerCalculations.plannedUnits +=
      (unitsPurchased * value.product.allocation) / 100.0;

    unspscCalculations.plannedSpend += incumbentOrCrossPlannedSpend;
    unspscCalculations.plannedUnits += allocatedUnits;

    // But sometimes it also goes into the excluded total because we may not be counting it when we
    // do measurement of the contract.
    if (excluded) {
      excludedPlannedSpend += incumbentOrCrossPlannedSpend;
      offerCalculations.excludedPlannedSpend += incumbentOrCrossPlannedSpend;

      unspscCalculations.excludedPlannedSpend += incumbentOrCrossPlannedSpend;
      unspscCalculations.excludedPlannedUnits += allocatedUnits;
    }

    product.offerCalculations.set(value.offerId, offerCalculations);
    product.unspscCalculations.set(
      incumbentOrCross.unspsc.id,
      unspscCalculations,
    );
  }

  product.calculations = {
    continuedSpend,
    continuedSpendAllocated,
    excludedPlannedSpend,
    plannedSpend,
    savings: savings(continuedSpend, plannedSpend),
  };

  return product;
}

interface ProductRollups {
  continuedSpend: number;
  continuedSpendAllocated: number;
  excludedPlannedSpend: number;
  filteredContinuedSpend: number;
  filteredContinuedSpendAllocated: number;
  filteredPlannedSpend: number;
  filteredExcludedPlannedSpend: number;
  plannedSpend: number;
  offerCalculations: Map<number | string, any>;
}

/**
 * Calculate a series of numbers across all of the products as a whole and just those visible with
 * the current filters as well.
 *
 * @param products
 */
function calculateProductRollups(products: any[]): ProductRollups {
  return products.reduce(
    (sum, product) => {
      sum.continuedSpend += product.calculations.continuedSpend;
      sum.continuedSpendAllocated +=
        product.calculations.continuedSpendAllocated;
      sum.plannedSpend += product.calculations.plannedSpend;
      sum.excludedPlannedSpend += product.calculations.excludedPlannedSpend;

      if (product.visible) {
        sum.filteredContinuedSpend += product.calculations.continuedSpend;
        sum.filteredContinuedSpendAllocated +=
          product.calculations.continuedSpendAllocated;
        sum.filteredPlannedSpend += product.calculations.plannedSpend;
        sum.filteredExcludedPlannedSpend +=
          product.calculations.excludedPlannedSpend;
      }

      // Go through all of the offer calculations and sum those up as well.
      for (let [key, value] of product.offerCalculations.entries()) {
        let offerCalculations = sum.offerCalculations.get(key) ?? {
          excludedPlannedSpend: 0,
          filteredExcludedPlannedSpend: 0,
          filteredPlannedSpend: 0,
          plannedSpend: 0,

          excludedPlannedUnits: 0,
          filteredExcludedPlannedUnits: 0,
          filteredPlannedUnits: 0,
          plannedUnits: 0,
        };

        offerCalculations.excludedPlannedSpend += value.excludedPlannedSpend;
        offerCalculations.plannedSpend += value.plannedSpend;

        offerCalculations.plannedUnits += value.plannedUnits;

        if (product.visible) {
          offerCalculations.filteredPlannedSpend += value.plannedSpend;
          offerCalculations.filteredExcludedPlannedSpend +=
            value.excludedPlannedSpend;

          offerCalculations.filteredPlannedUnits += value.plannedUnits;

          if (product.exclude) {
            offerCalculations.filteredExcludedPlannedSpend +=
              value.excludedPlannedSpend;
          }
        }

        sum.offerCalculations.set(key, offerCalculations);
      }

      return sum;
    },
    {
      continuedSpend: 0,
      continuedSpendAllocated: 0,
      excludedPlannedSpend: 0,
      filteredContinuedSpend: 0,
      filteredContinuedSpendAllocated: 0,
      filteredPlannedSpend: 0,
      filteredExcludedPlannedSpend: 0,
      plannedSpend: 0,
      offerCalculations: new Map<number | string, any>(),
    },
  );
}

interface OfferRollups {
  excludedPlannedSpend: number;
  filteredExcludedPlannedSpend: number;
  filteredPlannedSpend: number;
  plannedSpend: number;

  excludedPlannedUnits: number;
  filteredExcludedPlannedUnits: number;
  filteredPlannedUnits: number;
  plannedUnits: number;
}

/**
 * Calculate rolled up numbers for a single offer. Note: One of the things taken into account is that
 * we will not include UNSPSC data for UNSPSCs where the seller does not sell products.
 *
 * So, for example, if we're totaling things up and we find spend allocated to a product from another
 * seller, but it's a product in an UNSPSC where we don't have any products, then that won't go into
 * the totals because I (as a seller) didn't really lose out on a sale, I couldn't have gotten that
 * sale anyway.
 *
 * @param products
 * @param unspscs
 * @param offerId
 */
function calculateOfferRollups(
  products: any[],
  unspscs: any[],
  offerId: number | string,
): OfferRollups {
  // Get a list of all the UNSPSC IDs where this seller participates.
  let participatingUnspscs = unspscs
    .filter((unspsc) => {
      let coverage = getCoverageByOffer(unspsc, offerId);

      return coverage.participates;
    })
    .map((unspsc) => unspsc.unspsc.id);

  // This is a speedup for the most common case. When you have a lot of products (> 1500 in my testing)
  // this shortcut for testing whether a given amount of planned spend should be included saved about
  // 30-50ms on my machine. Not a huge amount, but it adds up over time.
  let participatesInAllUnspscs = participatingUnspscs.length === unspscs.length;

  return products.reduce(
    (sum: OfferRollups, product) => {
      for (let unspsc of product.unspscCalculations.keys()) {
        // If we have some planned spend (or units) for this product in an UNSPSC where the seller
        // participates, include it in the rollup.
        //
        // Note: Originally this keyed off of the UNSPSC of the incumbent product, now it keys off
        // of the UNSPSC(s) actually allocated to.
        if (participatesInAllUnspscs || participatingUnspscs.includes(unspsc)) {
          let unspscCalculations = product.unspscCalculations.get(unspsc);
          sum.excludedPlannedSpend += unspscCalculations.excludedPlannedSpend;
          sum.plannedSpend += unspscCalculations.plannedSpend;

          sum.excludedPlannedUnits += unspscCalculations.excludedPlannedUnits;
          sum.plannedUnits += unspscCalculations.plannedUnits;

          if (product.visible) {
            sum.filteredPlannedSpend += unspscCalculations.plannedSpend;
            sum.filteredExcludedPlannedSpend +=
              unspscCalculations.excludedPlannedSpend;

            sum.filteredPlannedUnits += unspscCalculations.plannedUnits;
            sum.filteredExcludedPlannedUnits +=
              unspscCalculations.excludedPlannedUnits;
          }
        }
      }

      return sum;
    },
    {
      excludedPlannedSpend: 0,
      filteredExcludedPlannedSpend: 0,
      filteredPlannedSpend: 0,
      plannedSpend: 0,

      excludedPlannedUnits: 0,
      filteredExcludedPlannedUnits: 0,
      filteredPlannedUnits: 0,
      plannedUnits: 0,
    },
  );
}

/**
 * Recalculate numbers at all the levels (product, offer, and plan) as well as refiltering as needed.
 *
 * @param state
 * @param action
 * @param recalculateProductNumbers
 * @param recalculateUnspscNumbers
 * @param recalculateOfferNumbers
 * @param recalculatePlanNumbers
 * @param refilter
 */
export function recalculateAndRefilter(
  state: any,
  action: any,
  recalculateProductNumbers: boolean,
  recalculateUnspscNumbers: boolean,
  recalculateOfferNumbers: boolean,
  recalculatePlanNumbers: boolean,
  refilter: boolean,
) {
  // console.time('recalculateAndRefilter');
  // console.group();

  let newState = produce(state, (draft) => {
    if (recalculateProductNumbers) {
      // console.time('recalculateProductNumbers');

      // Automatically trigger refiltering and recalculations at the UNSPSC level if we're going to redo product level
      // calculations.
      recalculateUnspscNumbers = true;
      refilter = true;

      if (action?.data.incumbentProductId) {
        // We really need to just update one product out of all of them.
        let reallocatedProduct = findIncumbentProduct(
          draft,
          action.data.incumbentProductId,
        );

        updateProductCalculations(reallocatedProduct);
      } else {
        // Let's update the calculations for all of our products.
        draft.products.forEach((product: any) =>
          updateProductCalculations(product),
        );
      }
      // console.timeEnd('recalculateProductNumbers');
    }

    // Filter the products based upon the current filter set.
    if (refilter) {
      // console.time('refilter');

      // You would think there would be no need to recalculate if all we did was refilter, but we calculate
      // separate totals for just the filtered set of products.
      recalculateUnspscNumbers = true;

      if (draft.products.length) {
        draft.uiGridProducts = productsToModel(draft.products, draft.offers);

        let incumbentSellersSet = new Set<number>();
        draft.products.forEach((product: any) => {
          incumbentSellersSet.add(product.incumbentProduct.seller.id);
        });

        let { gridRows, counts } = filterProducts(
          draft,
          draft.uiGridProducts,
          draft.filters,
          draft.unspscs.map((unspsc: any) => unspsc.unspsc.id),
          draft.offers.map((offer: any) => offer.seller.id),
          [...incumbentSellersSet.values()],
        );

        draft.filteredUiGridProducts = gridRows;
        draft.counts = Object.assign({}, draft.counts, counts);
      }
      // console.timeEnd('refilter');
    }

    if (recalculateUnspscNumbers) {
      recalculateOfferNumbers = true;
    }

    let productRollups: ProductRollups | null = null;

    if (recalculateOfferNumbers) {
      // console.time('recalculateOfferNumbers');

      recalculatePlanNumbers = true;

      if (productRollups === null) {
        productRollups = calculateProductRollups(draft.products);
      }

      let { filteredPlannedSpend, filteredExcludedPlannedSpend } =
        productRollups;

      draft.offers.forEach((offer: any) => {
        if (offer.id === 'incumbent') {
          let offerCalculations =
            productRollups.offerCalculations.get('incumbent');
          let total =
            offerCalculations?.filteredPlannedSpend -
            offerCalculations?.filteredExcludedPlannedSpend;

          // Note: This is actually a filtered allocated market share, so it may differ from what is
          // seen above the microcharts.
          offer.allocatedMarketShare = percentage(
            total,
            filteredPlannedSpend - filteredExcludedPlannedSpend,
          );
          offer.total = offerCalculations?.filteredPlannedSpend;
        } else {
          // This isn't the incumbent products, this offer is from a seller (or perhaps an
          // existing contract).
          let offerCalculations = productRollups.offerCalculations.get(
            offer.id,
          );
          let total =
            offerCalculations?.filteredPlannedSpend -
            offerCalculations?.filteredExcludedPlannedSpend;

          // All these numbers should be only for UNSPSCs where this seller participates.
          let offerRollups = calculateOfferRollups(
            draft.products,
            draft.unspscs,
            offer.id,
          );
          let plannedUnits = offerCalculations?.plannedUnits;

          // Adding this variable to fix the issue when filtering by UNSPSC. Ticket QA-2167
          let totalPlannedUnits =
            offerCalculations?.filteredPlannedUnits -
            offerCalculations?.filteredExcludedPlannedUnits;

          offer.allocatedUnitMarketShare = percentage(
            totalPlannedUnits,
            offerRollups.filteredPlannedUnits -
            offerRollups.filteredExcludedPlannedUnits,
          );
          offer.plannedUnits = plannedUnits;

          // Note: This is actually a filtered allocated market share, so it may differ from what is
          // seen above the microcharts. If we do this same calculation without using the filtered
          // numbers then we can update the microchart amounts in real time by updating the numbers
          // currently wired in up above each one.
          offer.allocatedMarketShare = percentage(
            total,
            offerRollups.filteredPlannedSpend -
            offerRollups.filteredExcludedPlannedSpend,
          );
          offer.total = offerCalculations?.filteredPlannedSpend;
        }
      });
      // console.timeEnd('recalculateOfferNumbers');
    }

    if (recalculatePlanNumbers) {
      // console.time('recalculatePlanNumbers');

      // Don't bother generating this if we already did it.
      if (productRollups === null) {
        productRollups = calculateProductRollups(draft.products);
      }

      let {
        continuedSpend,
        continuedSpendAllocated,
        excludedPlannedSpend,
        plannedSpend,
        filteredContinuedSpend,
        filteredContinuedSpendAllocated,
        filteredPlannedSpend,
      } = productRollups;

      // I think a good argument could be made that this number should be stored in the incumbent offer.
      let incumbentAllocatedMarketShare = percentage(
        draft.offers[0].total,
        plannedSpend,
      );

      draft.activePurchasePlan = Object.assign(
        {},
        draft.activePurchasePlan,
        productRollups,
        {
          complianceSpend: plannedSpend - excludedPlannedSpend,
          filteredSavings: savings(
            filteredContinuedSpendAllocated,
            filteredPlannedSpend,
          ),
          filteredSavingsPercentage:
            savingsCeilingPercentage(
              filteredContinuedSpendAllocated,
              filteredPlannedSpend,
            ),
          incumbentAllocatedMarketShare,
          productAllocationPercentage: Math.round(
            (continuedSpendAllocated / continuedSpend) * 100,
          ),
          savings: savings(continuedSpend, plannedSpend),
          savingsPercentage: savingsPercentage(continuedSpend, plannedSpend),
        },
      );

      // console.timeEnd('recalculatePlanNumbers');
    }
    // getIncumbentConversionProductCount(draft.products);
    draft.counts = Object.assign(
      {},
      draft.counts,
      getIncumbentConversionProductCount(draft.products),
    );
  });

  // console.groupEnd();
  // console.timeEnd('recalculateAndRefilter');

  return newState;
}

function getIncumbentConversionProductCount(products) {
  let incumbentInSpend = 0;
  let conversionInSpend = 0;
  let incumbentInExcluded = 0;
  let conversionInExcluded = 0;

  products.forEach((product) => {
    let isConversionFound = false;
    let isExcludedIncumbentFound = false;
    // if any product is conversion product increase conversion count.
    for (let value of incumbentAndCrosses(product)) {
      let product = value.product;

      if (isConversionProduct(product)) {
        conversionInSpend++;
        isConversionFound = true;
      }

      // conversion count for excluded product.
      if (product.exclude) {
        // console.log('included=', product.incumbentProductId);
        // console.log('product=', product.productId);
        if (isConversionProduct(product)) {
          conversionInExcluded++;
        }

        if (
          isIncumbentProduct(products) &&
          !isExcludedIncumbentFound &&
          product.allocation >= 0
        ) {
          incumbentInExcluded++;
          isExcludedIncumbentFound = true;
        }
      }
    }

    // no conversion product found then it's incumbent product.
    if (!isConversionFound) {
      incumbentInSpend++;
    }
  });

  return {
    incumbentInSpend,
    conversionInSpend,
    conversionInExcluded,
    incumbentInExcluded,
    incumbentInCompliance: incumbentInSpend - incumbentInExcluded,
    conversionInCompliance: conversionInSpend - conversionInExcluded,
  };
}
